import { TypedDocumentNode, gql, useLazyQuery } from '@apollo/client';
import { ReactNode, useMemo } from 'react';
import { FormattedNumber } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { LiveIndicator } from '@sorare/core/src/atoms/ui/LiveIndicator';
import { FOOTBALL_PLAY_PRO_FIXTURE_COMPETITION_CONTENDER_CONTENDER } from '@sorare/core/src/constants/__generated__/routes';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';

import { LeagueTeams as GenericLeagueTeams } from '@sorare/gameplay/src/components/league/LeagueTeams';
import { YourNextStep } from '@sorare/gameplay/src/components/league/YourNextStep';
import { isFixtureOpened } from '@sorare/gameplay/src/lib/so5';

import { LineupLayout } from 'components/lineup/LineupLayout';
import { So5Lineup } from 'components/lineup/So5Lineup';

import {
  LeagueTeamsQuery,
  LeagueTeamsQueryVariables,
} from './__generated__/index.graphql';

const LEAGUE_TEAMS_QUERY = gql`
  query LeagueTeamsQuery($slug: String!) {
    so5 {
      so5League(slug: $slug) {
        slug
        so5Fixture {
          slug
          ...isFixtureOpened_so5Fixture
        }
        mySo5LeaderboardContenders {
          slug
          so5Leaderboard {
            slug
            upsellSo5Leaderboard {
              slug
            }
          }
          ...FootballLineupLayout_so5LeaderboardContender
          ...FootballSo5Lineup_so5LeaderboardContender
        }
      }
    }
  }
  ${LineupLayout.fragments.so5LeaderboardContender}
  ${So5Lineup.fragments.so5LeaderboardContender}
  ${isFixtureOpened.fragments.so5Fixture}
` as TypedDocumentNode<LeagueTeamsQuery, LeagueTeamsQueryVariables>;

const LeagueStats = styled(Horizontal)`
  position: absolute;
  right: var(--container-padding);
  height: 44px;
`;

type LineupLayoutWrapperProps = {
  contenders?: LeagueTeamsQuery['so5']['so5League']['mySo5LeaderboardContenders'];
  placeholders: { slug: string }[];
  leagueSlug?: string;
  fixtureSlug?: string;
};
const LineupLayoutWrapper = ({
  contenders,
  placeholders,
  leagueSlug,
  fixtureSlug,
}: LineupLayoutWrapperProps) => {
  return (contenders || placeholders.map(({ slug }) => ({ key: slug }))).map(
    contenderWithPlaceholder => {
      const contender =
        'key' in contenderWithPlaceholder
          ? undefined
          : contenderWithPlaceholder;
      const placeholder =
        'key' in contenderWithPlaceholder
          ? contenderWithPlaceholder
          : undefined;
      const slug = contender?.slug || placeholder?.key;
      return (
        <LineupLayout
          id={slug}
          key={slug}
          to={
            leagueSlug &&
            fixtureSlug &&
            contender?.slug &&
            generatePath(
              FOOTBALL_PLAY_PRO_FIXTURE_COMPETITION_CONTENDER_CONTENDER,
              {
                competition: leagueSlug,
                fixture: fixtureSlug,
                contender: contender?.slug,
              }
            )
          }
          so5LeaderboardContender={contender}
        >
          <So5Lineup so5LeaderboardContender={contender} />
        </LineupLayout>
      );
    }
  );
};

type Props = {
  slug: string;
  children?: ReactNode;
  mySo5LeaderboardContenders: { slug: string }[];
  leagueName: string;
  leagueColor: string;
  leagueLogo: string;
};

export const LeagueTeams = ({
  slug,
  children,
  mySo5LeaderboardContenders,
  leagueName,
  leagueColor,
  leagueLogo,
}: Props) => {
  const {
    flags: { enableCompetitionUpsell = false },
  } = useFeatureFlags();

  const [fetch, { data }] = useLazyQuery(LEAGUE_TEAMS_QUERY, {
    variables: {
      slug,
    },
  });

  const so5League = data?.so5.so5League;
  const so5Fixture = so5League?.so5Fixture;
  const contenderWithLiveGamesCount =
    so5League?.mySo5LeaderboardContenders.filter(
      contender => contender.so5Lineup?.hasLiveGames
    )?.length;
  const upsellLeaderboard = so5League?.mySo5LeaderboardContenders?.find(
    l => !!l.so5Leaderboard.upsellSo5Leaderboard
  )?.so5Leaderboard.upsellSo5Leaderboard;
  const memoizedLineupLayoutWrapper = useMemo(() => {
    /** memoizing this prevent unecessary render from parent rerenders */
    return (
      <LineupLayoutWrapper
        contenders={so5League?.mySo5LeaderboardContenders}
        placeholders={mySo5LeaderboardContenders}
        leagueSlug={slug}
        fixtureSlug={so5League?.so5Fixture.slug}
      />
    );
  }, [so5League, slug, mySo5LeaderboardContenders]);

  return (
    <GenericLeagueTeams
      leagueSlug={slug}
      header={
        <>
          <Img src={leagueLogo} alt={leagueName} width="295" height="44" />
          {!!contenderWithLiveGamesCount && (
            <LeagueStats gap={0.5}>
              <LiveIndicator />
              <LabelS as="p" bold color="var(--c-nd-600)">
                <FormattedNumber value={contenderWithLiveGamesCount} />
              </LabelS>
            </LeagueStats>
          )}
        </>
      }
      leagueColor={leagueColor}
      onIsVisibleCallback={() => {
        if (mySo5LeaderboardContenders.length > 0) {
          fetch();
        }
      }}
    >
      {memoizedLineupLayoutWrapper}
      {enableCompetitionUpsell && so5Fixture && isFixtureOpened(so5Fixture) && (
        <YourNextStep so5LeaderboardSlug={upsellLeaderboard?.slug} />
      )}
      {children}
    </GenericLeagueTeams>
  );
};
